import { cn } from '@/lib/utils';
import { textVariants } from '@/src/components/Text/Text';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/src/components/shad/accordion';
import { Card } from '@/src/components/shad/card';
import { useCoinData } from '@/src/hooks/useCoinData';
import { formatNumber, getUniqueArrayByKey } from '@/src/utils';
import { useTranslation } from 'react-i18next';

interface ExtendedCoinInfo extends CoinRate {
  subcoins: CoinRate[] | undefined;
}

const CoinPrice = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.buysell.cryptoPrice.${key}`);
  const { coinData } = useCoinData({});

  const groupedSubCoins = coinData?.map((coin) => {
    const subcoins = coinData?.filter((item) => {
      return item?.coin__ticker === coin?.coin__ticker;
    });
    return {
      ...coin,
      subcoins: subcoins?.length > 1 ? subcoins : undefined,
    };
  });

  const uniqueGroupedSubCoins =
    (groupedSubCoins &&
      getUniqueArrayByKey<ExtendedCoinInfo>(
        groupedSubCoins as ExtendedCoinInfo[],
        'coin__ticker'
      )) ??
    [];

  return (
    <div className='hidden md:grid gap-3'>
      <div className='grid'>
        <span
          className={cn(
            textVariants({ size: 'xxl', font: 'semibold' }),
            'md:text-2xl text-lg'
          )}
        >
          {trans('title')}
        </span>
        <span className={textVariants({ variant: 'desc', size: 'sm' })}>
          {' '}
          {trans('desc')}
        </span>
      </div>
      <Card className='p-4 pt-1 grid gap-5 divide-y divide-gray-100 dark:divide-grey-500'>
        {uniqueGroupedSubCoins?.map((coin, index) =>
          coin?.subcoins === undefined ? (
            <CoinRender key={index} item={coin} />
          ) : (
            <Accordion
              key={index}
              type='single'
              collapsible
              className='w-full flex flex-col -my-4'
            >
              <AccordionItem value={coin?.coin__label} className=' rounded-md'>
                <AccordionTrigger
                  // className={cn(textVariants({ font: "semibold" }))}
                  className='pt-1'
                >
                  <CoinRender item={coin} />
                  {/* <div className="text-left">{item?.title}</div> */}
                </AccordionTrigger>
                <AccordionContent className='grid gap-2 rounded-b-md pb-0 py-4 px-3 dark:bg-grey-600 bg-gray-50 border border-gray-200 rounded-lg'>
                  {coin?.subcoins?.map((coin, index) => (
                    <SubCoinRender key={index} item={coin} />
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )
        )}
      </Card>
    </div>
  );
};

export default CoinPrice;

const CoinRender = ({ item }: { item: CoinRate }) => (
  <div
    key={item.coin__id}
    className='flex items-center justify-between pt-3 w-full '
  >
    <div className='flex items-center gap-1 flex-1'>
      <img
        className='h-7 w-7'
        src={item?.coin__image}
        alt={item?.coin__ticker}
      />
      <div className='grid'>
        <span className={textVariants({})}>{item?.coin__label}</span>
      </div>
    </div>
    <div className='md:ml-10 flex-1 text-left'>
      <span className={textVariants()}>
        ${formatNumber(item?.coin__current_price)}
      </span>
    </div>
    <div className='flex justify-end flex-1'>
      <span
        className={cn(
          textVariants(),
          item?.coin__price_change_percentage_24h.includes('-')
            ? 'text-red-500 dark:text-red-500'
            : 'text-green-500 dark:text-green-500'
        )}
      >
        {formatNumber(item?.coin__price_change_percentage_24h)}%
      </span>
    </div>
  </div>
);

const SubCoinRender = ({ item }: { item: CoinRate }) => (
  <div key={item.coin__id} className='grid grid-cols-3 pt-3 '>
    <div className='flex items-center gap-1'>
      <img
        className='h-7 w-7'
        src={item?.coin__network_image || item?.coin__image}
        alt={item?.coin__ticker}
      />
      <div className='grid'>
        <span className={textVariants({ size: 'sm' })}>
          {item?.coin__network_label}
        </span>
      </div>
    </div>
    <div className='md:ml-10'>
      <span className={textVariants()}>
        ${formatNumber(item?.coin__current_price)}
      </span>
    </div>
    <div className='flex justify-end'>
      <span
        className={cn(
          textVariants(),
          item?.coin__price_change_percentage_24h.includes('-')
            ? 'text-red-500 dark:text-red-500'
            : 'text-green-500 dark:text-green-500'
        )}
      >
        {formatNumber(item?.coin__price_change_percentage_24h)}%
      </span>
    </div>
  </div>
);
